/** Campos de fecha por colección de la API */
export const API_COLLECTION_DATE_FIELDS = Object.freeze({
  estudios: ['date'],
  prensa: ['date'],
  podcasts: ['date'],
  casos: ['fecha_inicio', 'fecha_termino'],
  eventos: ['date'],
})

/**
 * Parsea fechas en ISO, dd/mm/aaaa, dd-mm-aaaa u otros formatos reconocibles por Date.
 * @returns {number} timestamp o NaN
 */
export function parseDateValue(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return Number.NaN

  const dmy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/)
  if (dmy) {
    const [, day, month, year] = dmy
    return Date.parse(
      `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
    )
  }

  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (iso) {
    const [, year, month, day] = iso
    return Date.parse(
      `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
    )
  }

  return Date.parse(raw)
}

/** Devuelve el año (aaaa) de una fecha en cualquier formato reconocible. */
export function getYearFromDate(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''

  const dmy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/)
  if (dmy) return dmy[3]

  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (iso) return iso[1]

  const parsed = parseDateValue(raw)
  if (!Number.isNaN(parsed)) {
    return String(new Date(parsed).getFullYear())
  }

  return ''
}

/** Formatea una fecha a dd-mm-aaaa (ej. 14-11-2025). */
export function formatDateToDdMmYyyy(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''

  const dmyDash = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/)
  if (dmyDash) {
    const [, day, month, year] = dmyDash
    return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`
  }

  const dmySlash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (dmySlash) {
    const [, day, month, year] = dmySlash
    return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`
  }

  const parsed = parseDateValue(raw)
  if (Number.isNaN(parsed)) return raw

  const date = new Date(parsed)
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

/** Normaliza fechas de ítems de una colección de la API. */
export function normalizeApiCollectionDates(items, collection) {
  const fields = API_COLLECTION_DATE_FIELDS[collection]
  if (!fields?.length) return Array.isArray(items) ? items : []
  if (!Array.isArray(items)) return []

  return items.map((item) => {
    if (!item || typeof item !== 'object') return item
    const out = { ...item }
    for (const field of fields) {
      if (out[field] != null && String(out[field]).trim() !== '') {
        out[field] = formatDateToDdMmYyyy(out[field])
      }
    }
    return out
  })
}
